import React, { useEffect, useMemo } from "react";
import { Col, Divider, Empty, Form, FormInstance, Input, Radio, Row, Select, Space, Spin } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { map, trimEnd, isEmpty, find } from "lodash";
import { PromotionNamePrefix, SaveOfferType } from "../../../../../types/model/saveOffer";
import styles from "./index.module.scss";
import { DropDown } from "../../../../../assets/icons";
import { PromotionDiscountType, SubscriptionPromotion } from "../../../../../types/model/promotion";
import { getDisplayDiscount, getDisplayDuration, getDisplayPromotionDiscountType } from "../../../Promotion/util";
import { EMPTY } from "../../../../../constants/common";

interface SaveOfferPromotionProps {
  form: FormInstance;
  selectedSaveOfferType: SaveOfferType;
  isPromotionListLoading: boolean;
  promotionList: SubscriptionPromotion[];
  selectedPromotion: SubscriptionPromotion;
  setSelectedPromotion: React.Dispatch<React.SetStateAction<SubscriptionPromotion>>;
}

export function SaveOfferPromotion({
  form,
  selectedSaveOfferType,
  isPromotionListLoading,
  promotionList,
  selectedPromotion,
  setSelectedPromotion,
}: SaveOfferPromotionProps) {
  const namePrefix = useMemo(
    () =>
      selectedSaveOfferType === SaveOfferType.PROMOTION ? PromotionNamePrefix.BasePrice : PromotionNamePrefix.Plan,
    [selectedSaveOfferType]
  );
  const hasPromotionCountries = useMemo(
    () => selectedPromotion?.appliedCountries && selectedPromotion?.appliedCountries?.length > 0,
    [selectedPromotion]
  );

  useEffect(() => {
    if (selectedPromotion) {
      const {
        discountType,
        duration = undefined,
        appliedCountries = [],
        currencies = [],
        percent = undefined,
      } = selectedPromotion;

      const countryNames = hasPromotionCountries ? map(appliedCountries, "name") : EMPTY;
      const discount = getDisplayDiscount(discountType, percent, currencies);
      const formattedDiscount =
        discountType === PromotionDiscountType.PERCENTAGE_OFF ? trimEnd(discount, "%") : discount;

      form.setFieldsValue({
        [`${namePrefix}Duration`]: getDisplayDuration(duration),
        [`${namePrefix}CountryIds`]: countryNames,
        [`${namePrefix}DiscountType`]: discountType,
        [`${namePrefix}AmountDiscount`]: formattedDiscount,
        [`${namePrefix}PercentageDiscount`]: formattedDiscount,
      });
    } else {
      resetPromotionDetailFields(form, namePrefix);
    }
  }, [selectedSaveOfferType, selectedPromotion, form, namePrefix]);

  const resetPromotionDetailFields = (form: FormInstance, namePrefix: string) => {
    form.resetFields([
      `${namePrefix}Duration`,
      `${namePrefix}CountryIds`,
      `${namePrefix}PercentageDiscount`,
      `${namePrefix}AmountDiscount`,
      `${namePrefix}DiscountType`,
    ]);
  };

  const getPromotionCodeTooltip = () => {
    return {
      title:
        selectedSaveOfferType === SaveOfferType.SWITCH_PLAN ? (
          <div>Active promotion codes that applied to the plan that switch to.</div>
        ) : (
          <div>
            You can only set up promotions for subscription plans (including all access). Remember, promotions need to
            be active and available to both new and existing subscribers.
          </div>
        ),
      icon: <InfoCircleOutlined className={styles.infoIcon} />,
    };
  };

  const handlePromotionCodeChange = (selectCode: string) => {
    const promotion = find(promotionList, { code: selectCode });
    setSelectedPromotion(promotion!);
  };

  return (
    <>
      <Divider className={styles.divider} />
      <Row gutter={32}>
        <Col span={6}>
          <Form.Item
            label="Promotion code"
            name={`${namePrefix}PromotionCode`}
            rules={[{ required: selectedSaveOfferType === SaveOfferType.PROMOTION }]}
            tooltip={getPromotionCodeTooltip()}
          >
            <Select
              showArrow
              allowClear
              notFoundContent={
                isPromotionListLoading ? (
                  <div style={{ height: "120px" }}>
                    <Spin size="small" />
                  </div>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              }
              suffixIcon={<DropDown />}
              options={promotionList.map(({ code }) => ({
                label: code,
                value: code,
              }))}
              onChange={handlePromotionCodeChange}
            />
          </Form.Item>
        </Col>
        {!isEmpty(selectedPromotion) && (
          <>
            <Col span={6}>
              <Form.Item label="Duration">
                <Space size={22} align="baseline" className={styles.littleInput}>
                  <Form.Item noStyle name={`${namePrefix}Duration`}>
                    <Input disabled={true} />
                  </Form.Item>
                </Space>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Applied country/region"
                name={`${namePrefix}CountryIds`}
                className={hasPromotionCountries ? styles.largeFormItem : undefined}
              >
                <Select
                  mode={hasPromotionCountries ? "multiple" : undefined}
                  disabled
                  showArrow
                  suffixIcon={<DropDown />}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Promotion type" name={`${namePrefix}DiscountType`}>
                <Radio.Group disabled>
                  {Object.values(PromotionDiscountType)
                    .filter((item) => item !== PromotionDiscountType.BUY_ONE_GIFT_ONE)
                    .map((type) => (
                      <Radio value={type} key={type}>
                        {getDisplayPromotionDiscountType(type)}
                      </Radio>
                    ))}
                </Radio.Group>
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate>
              {selectedPromotion.discountType === PromotionDiscountType.AMOUNT_OFF && (
                <Col span={18}>
                  <Form.Item label="Discount" name={`${namePrefix}AmountDiscount`}>
                    <Input disabled />
                  </Form.Item>
                </Col>
              )}
              {selectedPromotion.discountType === PromotionDiscountType.PERCENTAGE_OFF && (
                <Col span={6}>
                  <Form.Item label="Discount" name={`${namePrefix}PercentageDiscount`}>
                    <Input disabled addonAfter="%" />
                  </Form.Item>
                </Col>
              )}
            </Form.Item>
          </>
        )}
      </Row>
    </>
  );
}
